/**
 * Client routes for the GUI.
 *
 * Tabs and drill-down screens used to live only in React state (and a
 * localStorage last-tab key). The address bar never changed, so the browser
 * Back button — and any in-app back that fell through to history — left the
 * app instead of the previous screen. The FastAPI catch-all already serves
 * index.html for unknown paths; this module is the routing that catch-all
 * was waiting for.
 */

export type Tab =
  | 'overview'
  | 'campaigns'
  | 'filters'
  | 'people'
  | 'accounts'
  | 'emails'
  | 'styles'
  | 'library'
  | 'improvement'
  | 'settings'

export const TABS: { id: Tab; label: string; title?: string }[] = [
  { id: 'overview', label: 'Overview', title: 'What this is and how the tabs fit together' },
  { id: 'campaigns', label: 'Campaigns', title: 'The standing programs and their cycles' },
  { id: 'filters', label: 'Filters', title: 'Named audiences a campaign is pointed at' },
  { id: 'people', label: 'People', title: 'Count an audience; look at a saved list' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'emails', label: 'Emails', title: 'Every draft across every cycle — the review backlog' },
  { id: 'styles', label: 'Styles' },
  { id: 'library', label: 'Library' },
  { id: 'improvement', label: 'Improvement' },
  { id: 'settings', label: 'Settings' },
]

export const TAB_IDS = new Set<string>(TABS.map((t) => t.id))

export type Route = {
  tab: Tab
  campaignId: number | null
  cycleId: number | null
  creating: boolean
  filter: string | null
  draftId: number | null
}

export const LAST_TAB_KEY = 'reachout.tab'

export function routeForTab(tab: Tab): Route {
  return {
    tab,
    campaignId: null,
    cycleId: null,
    creating: false,
    filter: null,
    draftId: null,
  }
}

function isId(value: string | undefined): value is string {
  return !!value && /^\d+$/.test(value)
}

export function parsePath(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean)
  const head = parts[0]

  if (!head || head === 'overview') return routeForTab('overview')

  // A cycle opened from the stuck panel has no campaign in the URL yet.
  if (head === 'cycles' && isId(parts[1])) {
    return { ...routeForTab('campaigns'), cycleId: Number(parts[1]) }
  }

  if (head === 'campaigns') {
    if (parts[1] === 'new') return { ...routeForTab('campaigns'), creating: true }
    if (isId(parts[1])) {
      const campaignId = Number(parts[1])
      if (parts[2] === 'cycles' && isId(parts[3])) {
        return { ...routeForTab('campaigns'), campaignId, cycleId: Number(parts[3]) }
      }
      return { ...routeForTab('campaigns'), campaignId }
    }
    return routeForTab('campaigns')
  }

  if (head === 'filters') {
    return {
      ...routeForTab('filters'),
      filter: parts[1] ? decodeURIComponent(parts[1]) : null,
    }
  }

  if (head === 'emails') {
    return {
      ...routeForTab('emails'),
      draftId: isId(parts[1]) ? Number(parts[1]) : null,
    }
  }

  if (TAB_IDS.has(head)) return routeForTab(head as Tab)
  return routeForTab('overview')
}

export function toPath(route: Route): string {
  switch (route.tab) {
    case 'campaigns':
      if (route.cycleId != null && route.campaignId != null) {
        return `/campaigns/${route.campaignId}/cycles/${route.cycleId}`
      }
      if (route.cycleId != null) return `/cycles/${route.cycleId}`
      if (route.creating) return '/campaigns/new'
      if (route.campaignId != null) return `/campaigns/${route.campaignId}`
      return '/campaigns'
    case 'filters':
      return route.filter ? `/filters/${encodeURIComponent(route.filter)}` : '/filters'
    case 'emails':
      return route.draftId != null ? `/emails/${route.draftId}` : '/emails'
    default:
      return `/${route.tab}`
  }
}

export function readInitialRoute(): Route {
  const path = window.location.pathname
  if (path === '/' || path === '') {
    try {
      const saved = window.localStorage.getItem(LAST_TAB_KEY)
      if (saved && TAB_IDS.has(saved)) return routeForTab(saved as Tab)
    } catch {
      // Private windows and blocked site data both throw here.
    }
    return routeForTab('overview')
  }
  return parsePath(path)
}
